/**
 * @fileoverview PdfViewer
 *
 * Purpose: Render a PDF inline using pdfjs-dist instead of an embedded
 * `<iframe>` to Chrome's native viewer. Crucially, pdfjs-dist does NOT
 * execute embedded JavaScript actions (e.g. `OpenAction → app.alert(...)`)
 * that government / ATO forms commonly bake in, so we never see the
 * "Please save this document on your local desktop..." native modal that
 * the Chrome viewer fires on open.
 *
 * Renders all pages stacked vertically inside a scrollable container,
 * sized to fit the container width. Supports devicePixelRatio-aware
 * sharpness for retina displays.
 *
 * Used by:
 * - SourceDocPreview (settings precedent preview)
 * - Day-one filled PDF / fallback PDF previews
 */

import { useEffect, useRef, useState } from "react";
import { pdfjsLib } from "@/utils/pdf/pdfjs";
import { Loader2, Lock } from "lucide-react";
import { cn } from "@/utils/misc";

interface PdfViewerProps {
  /** URL of the PDF to render. Should be same-origin or CORS-enabled. */
  url: string;
  /** Optional accessible label / title (rendered as the canvas title). */
  title?: string;
  className?: string;
  /**
   * Optional render-prop for overlaying interactive content on top of
   * each page (clickable checkboxes, hot zones, annotations). Receives
   * the page number plus the page's CSS dimensions and original PDF
   * dimensions in points so coordinate-space callers can transform
   * placements without recomputing the scale themselves.
   */
  renderPageOverlay?: (page: {
    pageNumber: number;
    cssWidth: number;
    cssHeight: number;
    pdfWidthPts: number;
    pdfHeightPts: number;
  }) => React.ReactNode;
}

export function PdfViewer({
  url,
  title,
  className,
  renderPageOverlay,
}: PdfViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  // Keep the previous PDF on screen while a new URL loads so users editing
  // a form (e.g. day-one ATO checkbox toggles) get instant visual continuity
  // instead of a full spinner-and-reload cycle on every change (MOR-458).
  const [isReloading, setIsReloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setIsReloading(true);
    (async () => {
      try {
        // enableScripting defaults to false — leaving it that way is what
        // suppresses AcroForm OpenAction alerts.
        const doc = await pdfjsLib.getDocument({ url }).promise;
        if (cancelled) {
          doc.loadingTask.destroy();
          return;
        }
        setPdfDoc((prev) => {
          // Tear down the previous doc only after we have a replacement so
          // pdf.js never sees a torn-down doc mid-render.
          if (prev && prev !== doc) prev.loadingTask.destroy();
          return doc;
        });
      } catch (err) {
        if (cancelled) return;
        console.error("[PdfViewer] failed to load", err);
        setError(err instanceof Error ? err.message : "Failed to load PDF");
      } finally {
        if (!cancelled) setIsReloading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  // Destroy the final pdfDoc on unmount. (The per-url effect handles
  // freeing intermediate docs as we swap; this catches the last one.)
  useEffect(() => {
    return () => {
      setPdfDoc((prev) => {
        prev?.loadingTask.destroy();
        return null;
      });
    };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (error) {
    const isPasswordError = /password/i.test(error);
    return (
      <div
        className={cn("flex h-full items-center justify-center p-8", className)}
      >
        <div className="flex w-full max-w-md items-start gap-3 rounded-lg border border-amber-200/60 bg-amber-50 px-4 py-3.5 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          <Lock className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            {isPasswordError ? (
              <>
                <p className="font-medium leading-snug">
                  This PDF appears to be password‑protected or has security
                  restrictions that prevent it from being previewed.
                </p>
                <p className="text-xs leading-relaxed text-amber-900/80 dark:text-amber-200/80">
                  Even if you weren't asked for a password, the file can carry
                  encryption or permission flags (often added automatically by
                  scanners, banks, or government portals) that block viewers
                  from opening it.
                </p>
                <div className="text-xs leading-relaxed text-amber-900/80 dark:text-amber-200/80">
                  <p className="mb-1 font-medium text-amber-900 dark:text-amber-200">
                    How to fix it:
                  </p>
                  <ol className="ml-4 list-decimal space-y-0.5">
                    <li>Open the PDF in Adobe Acrobat, Preview, or Chrome.</li>
                    <li>
                      Print it to PDF (File → Print → Save as PDF), or export it
                      as a new PDF.
                    </li>
                    <li>Re‑upload the unrestricted copy here.</li>
                  </ol>
                </div>
              </>
            ) : (
              <>
                <p className="font-medium leading-snug">
                  Could not load this PDF.
                </p>
                <p className="text-xs leading-relaxed text-amber-900/80 dark:text-amber-200/80">
                  The file may be corrupted, restricted, or saved in an
                  unsupported format. Try re‑exporting it from Adobe Acrobat or
                  Preview and uploading the new copy.
                </p>
              </>
            )}
            <p className="break-all pt-1 font-mono text-[10px] text-amber-900/60 dark:text-amber-200/60">
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative h-full w-full overflow-auto bg-muted/40",
        className,
      )}
    >
      {pdfDoc ? (
        <div className="mx-auto flex flex-col items-center gap-3 p-3">
          {Array.from({ length: pdfDoc.numPages }, (_, i) => (
            <PdfPage
              key={i}
              pdfDoc={pdfDoc}
              pageNumber={i + 1}
              containerWidth={containerWidth}
              title={title}
              renderOverlay={renderPageOverlay}
            />
          ))}
        </div>
      ) : (
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      {pdfDoc && isReloading && (
        <div className="pointer-events-none sticky top-2 z-10 ml-auto mr-2 flex w-fit items-center gap-1.5 rounded-full bg-background/90 px-2.5 py-1 text-[11px] font-medium text-muted-foreground shadow-sm ring-1 ring-border/60 backdrop-blur">
          <Loader2 className="h-3 w-3 animate-spin" />
          Updating…
        </div>
      )}
    </div>
  );
}

function PdfPage({
  pdfDoc,
  pageNumber,
  containerWidth,
  title,
  renderOverlay,
}: {
  pdfDoc: pdfjsLib.PDFDocumentProxy;
  pageNumber: number;
  containerWidth: number;
  title?: string;
  renderOverlay?: (page: {
    pageNumber: number;
    cssWidth: number;
    cssHeight: number;
    pdfWidthPts: number;
    pdfHeightPts: number;
  }) => React.ReactNode;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<{ cancel(): void } | null>(null);
  const [size, setSize] = useState<{
    w: number;
    h: number;
    pdfWidthPts: number;
    pdfHeightPts: number;
  } | null>(null);

  useEffect(() => {
    if (!containerWidth) return;
    let cancelled = false;

    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    (async () => {
      try {
        const page = await pdfDoc.getPage(pageNumber);
        if (cancelled) return;

        // Fit page to container width minus padding (12px gutter on each
        // side from the parent's p-3) and respect devicePixelRatio so the
        // rendered canvas stays crisp on retina screens.
        const targetCssWidth = Math.max(200, containerWidth - 24);
        const baseViewport = page.getViewport({ scale: 1 });
        const cssScale = targetCssWidth / baseViewport.width;
        const dpr =
          typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
        const viewport = page.getViewport({ scale: cssScale * dpr });

        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const cssHeight =
          (baseViewport.height / baseViewport.width) * targetCssWidth;
        setSize({
          w: targetCssWidth,
          h: cssHeight,
          pdfWidthPts: baseViewport.width,
          pdfHeightPts: baseViewport.height,
        });

        const ctx = canvas.getContext("2d");
        if (!ctx || cancelled) return;

        const task = page.render({
          canvas,
          canvasContext: ctx,
          viewport,
        });
        renderTaskRef.current = task;
        await task.promise;
      } catch (err) {
        if (cancelled) return;
        const isCancellation =
          err instanceof Error && /cancel/i.test(err.message);
        if (!isCancellation) {
          console.warn("[PdfViewer] page render failed", err);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, pageNumber, containerWidth]);

  return (
    <div
      className="relative bg-white shadow"
      style={size ? { width: size.w, height: size.h } : undefined}
    >
      <canvas
        ref={canvasRef}
        className="block h-full w-full"
        title={title ? `${title} — page ${pageNumber}` : `Page ${pageNumber}`}
      />
      {size && renderOverlay && (
        <div className="pointer-events-none absolute inset-0">
          {renderOverlay({
            pageNumber,
            cssWidth: size.w,
            cssHeight: size.h,
            pdfWidthPts: size.pdfWidthPts,
            pdfHeightPts: size.pdfHeightPts,
          })}
        </div>
      )}
    </div>
  );
}
